import { CircularProgress } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { toast } from "react-toastify";

interface Permissions {
    view: boolean;
    edit: boolean;
    add: boolean;
    delete: boolean;
}

interface FormData {
    role: string;
    permissions: {
        [key: string]: Permissions;
    };
}

type ApiError = {
    response?: {
        data?: {
            message?: string;
        };
    };
    message?: string;
};

interface PermissionsFormProps {
    modalDisplay: boolean;
    createNew: boolean;
    onClose: () => void;
    onRoleCreated: () => void;
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
}

const PermissionsForm = ({
    modalDisplay,
    createNew,
    onClose,
    onRoleCreated,
    isLoading,
    setIsLoading,
}: PermissionsFormProps) => {
    const token = window.localStorage.getItem("token");
    const [reLogin, setReLogin] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        role: "",
        permissions: {
            activitylogs: { view: false, edit: false, add: false, delete: false },
            bank: { view: true, edit: true, add: true, delete: false },
            branch: { view: false, edit: false, add: false, delete: false },
            coupon: { view: false, edit: false, add: false, delete: false },
            customer: { view: false, edit: false, add: false, delete: false },
            customeraccount: { view: false, edit: false, add: false, delete: false },
            customerpaymenthistory: { view: false, edit: false, add: false, delete: false },
            inventory: { view: false, edit: false, add: false, delete: false },
            invoice: { view: true, edit: true, add: true, delete: false },
            loadingslip: { view: true, edit: false, add: true, delete: false },
            loyalty: { view: false, edit: false, add: false, delete: false },
            registeredtrucks: { view: false, edit: false, add: false, delete: false },
            reports: { view: false, edit: false, add: false, delete: false },
            requisition: { view: true, edit: true, add: true, delete: true },
            roles: { view: false, edit: false, add: false, delete: false },
            settings: { view: false, edit: false, add: false, delete: false },
            staff: { view: false, edit: false, add: false, delete: false },
            subscription: { view: false, edit: false, add: false, delete: false },
            transaction: { view: false, edit: false, add: false, delete: false },
            warehouse: { view: true, edit: false, add: true, delete: true },
        },
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const categories = [
        "activitylogs",
        "bank",
        "branch",
        "coupon",
        "customer",
        "customeraccount",
        "customerpaymenthistory",
        "inventory",
        "invoice",
        "loadingslip",
        "loyalty",
        "registeredtrucks",
        "reports",
        "requisition",
        "roles",
        "settings",
        "staff",
        "subscription",
        "transaction",
        "warehouse",
    ];

    const handlePermissionChange = (category: string, permission: keyof Permissions, value: boolean) => {
        const currentPermissions = formData.permissions[category];

        if (permission !== "view" && value && !currentPermissions.view) {
            setErrors((prev) => ({ ...prev, [category]: "Cannot select " + permission + " without first selecting View." }));
            return;
        }

        if (permission === "view" && !value) {
            // Uncheck all others if View is unchecked
            setFormData((prev) => ({
                ...prev,
                permissions: {
                    ...prev.permissions,
                    [category]: {
                        view: false,
                        edit: false,
                        add: false,
                        delete: false,
                    },
                },
            }));
            setErrors((prev) => ({ ...prev, [category]: "" }));
        } else {
            setFormData((prev) => ({
                ...prev,
                permissions: {
                    ...prev.permissions,
                    [category]: {
                        ...prev.permissions[category],
                        [permission]: value,
                    },
                },
            }));
            setErrors((prev) => ({ ...prev, [category]: "" }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.role.trim()) {
            toast.error("Role is required");
            return;
        }
        setIsLoading(true);

        try {
            const response = await axios.post(
                "https://apibiz.oneflaretech.com/api/roles",
                {
                    role: formData.role,
                    permissions: formData.permissions,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success("Role created successfully!");
            onRoleCreated();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to create role");
            const err = error as ApiError;
            const errorMessage =
                err.response?.data?.message || err.message || "Failed to create role";
            if (errorMessage.includes("jwt expired") || errorMessage.includes("jwt malformed")) {
                setReLogin(true);
                onClose();
            } else {
                setReLogin(false);
            }
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const getSelectedPermissions = (category: string) => {
        const perms = formData.permissions[category];
        return Object.keys(perms)
            .filter((key) => perms[key as keyof Permissions])
            .map((key) => key.charAt(0).toUpperCase() + key.slice(1))
            .join(", ");
    };

    return (
        <div className="invoice-modal-content" style={{ padding: "20px" }}>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>
                        Role <span className="text-red-600">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.role}
                        required
                        onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            outline: "none",
                        }}
                    />
                </div>
                {categories.map((category) => (
                    <details key={category} style={{ marginBottom: "10px" }}>
                        <summary className="py-2 px-1 border-b rounded-b-lg cursor-pointer">
                            {category.charAt(0).toUpperCase() + category.slice(1)}{" "}
                            <span style={{ color: "blue" }}>{getSelectedPermissions(category)}</span>
                        </summary>
                        <div style={{ padding: "10px", border: "1px solid #ccc", borderRadius: "4px", marginTop: "5px" }}>
                            <div>
                                <label className="flex justify-between p-2 hover:bg-gray-100 cursor-pointer">
                                    User is permitted to View
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions[category].view}
                                        onChange={(e) => handlePermissionChange(category, "view", e.target.checked)}
                                    />
                                </label>
                            </div>
                            <div>
                                <label className="flex justify-between p-2 hover:bg-gray-100 cursor-pointer">
                                    User is permitted to Edit
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions[category].edit}
                                        onChange={(e) => handlePermissionChange(category, "edit", e.target.checked)}
                                    />
                                </label>
                            </div>
                            <div>
                                <label className="flex justify-between p-2 hover:bg-gray-100 cursor-pointer">
                                    User is permitted to Add
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions[category].add}
                                        onChange={(e) => handlePermissionChange(category, "add", e.target.checked)}
                                    />
                                </label>
                            </div>
                            <div>
                                <label className="flex justify-between p-2 hover:bg-gray-100 cursor-pointer">
                                    User is permitted to Delete
                                    <input
                                        type="checkbox"
                                        checked={formData.permissions[category].delete}
                                        onChange={(e) => handlePermissionChange(category, "delete", e.target.checked)}
                                    />
                                </label>
                            </div>
                            {errors[category] && <p style={{ color: "red", marginTop: "10px" }}>{errors[category]}</p>}
                        </div>
                    </details>
                ))}
                <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    <button
                        type="submit"
                        style={{
                            padding: "10px 20px",
                            backgroundColor: isLoading ? "#0B77C5" : "#0B77C5",
                            color: "#fff",
                            border: "none",
                            width: "50%",
                            borderRadius: "4px",
                        }}
                    >
                        {isLoading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Submit"}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onClose();
                            setFormData({
                                role: "",
                                permissions: {
                                    warehouse: { view: true, edit: false, add: true, delete: true },
                                    inventory: { view: false, edit: false, add: false, delete: false },
                                    transaction: { view: false, edit: false, add: false, delete: false },
                                    staff: { view: false, edit: false, add: false, delete: false },
                                    customer: { view: false, edit: false, add: false, delete: false },
                                    requisition: { view: false, edit: false, add: false, delete: false },
                                    customeraccount: { view: false, edit: false, add: false, delete: false },
                                    customerpaymenthistory: { view: false, edit: false, add: false, delete: false },
                                    roles: { view: false, edit: false, add: false, delete: false },
                                    reports: { view: false, edit: false, add: false, delete: false },
                                    registeredtrucks: { view: false, edit: false, add: false, delete: false },
                                    subscription: { view: false, edit: false, add: false, delete: false },
                                    branch: { view: false, edit: false, add: false, delete: false },
                                    settings: { view: false, edit: false, add: false, delete: false },
                                    loadingslip: { view: false, edit: false, add: false, delete: false },
                                    invoice: { view: false, edit: false, add: false, delete: false },
                                    coupon: { view: false, edit: false, add: false, delete: false },
                                    activitylogs: { view: false, edit: false, add: false, delete: false },
                                    bank: { view: false, edit: false, add: false, delete: false },
                                    loyalty: { view: false, edit: false, add: false, delete: false },
                                },
                            });
                            setErrors({});
                        }}
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#ccc",
                            color: "#000",
                            width: "50%",
                            border: "none",
                            borderRadius: "4px",
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PermissionsForm;